/**
 * Règles de circulation — **logique pure**, aucune dépendance au rendu.
 *
 * ══ Pourquoi ce module existe ════════════════════════════════════════════════
 *
 * Le trafic du jeu était un peuplement : des sprites parcouraient un itinéraire tiré au
 * hasard, au milieu de la chaussée, en se traversant aux carrefours. Rien ne s'arrêtait
 * jamais. Une ville n'est pas un décor où des voitures glissent — c'est un système de
 * priorités, et c'est le fait de voir une voiture **attendre** puis **repartir** qui
 * raconte qu'il y a quelqu'un au volant.
 *
 * Trois règles, et elles suffisent à faire lire une circulation :
 *
 * 1. **On roule à droite.** Deux véhicules en sens inverse se croisent au lieu de se
 *    superposer. C'est la règle qui rend le trafic lisible à l'œil avant toute autre.
 * 2. **Un carrefour à quatre branches est réglé par un FEU**, dont le cycle alterne les
 *    deux axes.
 * 3. **Un carrefour à trois branches est réglé par un STOP** posé sur la branche
 *    secondaire — celle qui vient buter sur la route principale.
 *
 * ══ Le temps est FOURNI, jamais lu ═══════════════════════════════════════════
 *
 * Comme `TweenRunner`, ce module reçoit l'horloge de son appelant. Deux conséquences qui
 * valent d'être écrites : la couleur d'un feu est une **fonction pure du temps et de la
 * case**, donc testable sans navigateur ; et un onglet masqué pendant deux minutes
 * retrouve des feux cohérents au lieu de rattraper un retard accumulé.
 *
 * ══ Les feux ne sont pas synchronisés, et c'est délibéré ═════════════════════
 *
 * Le déphasage se dérive des coordonnées de la case. Des feux qui passeraient tous au vert
 * ensemble se liraient comme une animation unique plutôt que comme une ville — et une
 * ville dont tous les carrefours battent la même mesure est le genre de détail qui donne
 * l'impression d'un décor sans qu'on sache dire pourquoi.
 */

import { EAST, NORTH, SOUTH, WEST } from './roads';

/** Durées d'un cycle de feu, en millisecondes. */
export const LIGHT_CYCLE = {
  /**
   * Vert pour un axe. Assez long pour laisser passer deux ou trois véhicules.
   *
   * Raccourci de 6 s à 4 s (18 août) : l'attente au pire cas passait 9 s, et une
   * voiture arrêtée 9 s dans un jeu se lit comme une voiture en panne — c'est le
   * « les véhicules se bloquent » rapporté par Benoit, mesuré sans aucun blocage
   * réel par `probe:traffic`. Le pire cas tombe à 5,8 s.
   */
  greenMs: 4000,
  /**
   * Tout-au-rouge entre deux verts.
   *
   * Sans lui, un véhicule qui vient de démarrer croise celui qui finit de traverser.
   * Une petite seconde suffit à vider le carrefour à la vitesse du jeu, et c'est ce
   * battement d'arrêt qui rend le feu crédible.
   */
  clearMs: 900,
} as const;

/** Un cycle complet couvre les deux axes. */
export const LIGHT_PERIOD_MS = 2 * (LIGHT_CYCLE.greenMs + LIGHT_CYCLE.clearMs);

/** Les deux axes d'un carrefour. L'axe d'une direction est celui qu'elle emprunte. */
export type Axis = 'ns' | 'eo';

export function axisOf(direction: number): Axis {
  return direction === 0 || direction === 2 ? 'ns' : 'eo';
}

/**
 * Type de contrôle d'une case du réseau, déduit de son masque de connexions.
 *
 * `null` : rien à respecter — une case de route ordinaire, un virage, une impasse. Un
 * virage n'est pas un carrefour : rien n'y croise personne.
 */
export type Control = { readonly kind: 'light' } | { readonly kind: 'stop' } | null;

function bitsOf(mask: number): number[] {
  return [NORTH, EAST, SOUTH, WEST].filter((bit) => (mask & bit) !== 0);
}

/**
 * Contrôle applicable à une case, dérivé de son masque ET de sa position.
 *
 * Dérivé et non stocké : le réseau change à chaque bâtiment posé, et une table de feux
 * tenue à part divergerait du tracé au premier élagage de rue (`pruneRoads`).
 *
 * ── Pourquoi les T portent aussi des feux (18 août) ──────────────────────────
 *
 * Le traceur automatique construit des réseaux en ARBRE : aucun cycle, donc aucun
 * carrefour à quatre branches — mesuré sur la ville de Benoit (115 cases de route,
 * 8 carrefours, TOUS en T) comme sur la ville synthétique des sondes. La règle
 * « feu si 4 branches » était donc du code mort : pas un joueur n'a jamais vu un
 * feu tricolore. Les T alternent désormais feu et stop, par la parité de leur
 * position — déterministe, dérivé, et les deux signalisations existent à l'écran.
 */
export function controlFor(mask: number, col: number, row: number): Control {
  const count = bitsOf(mask).length;
  if (count >= 4) return { kind: 'light' };
  if (count === 3) {
    return (((col + row) % 2) + 2) % 2 === 0 ? { kind: 'light' } : { kind: 'stop' };
  }
  return null;
}

/**
 * Déphasage d'un feu, en millisecondes, dérivé de la position de la case.
 *
 * Deux facteurs premiers pour que le motif ne se répète pas visiblement le long d'une
 * avenue — même raison que pour l'orientation des bâtiments (`orientationFor`).
 */
export function lightPhase(col: number, row: number): number {
  return (((col * 3407 + row * 5099) % LIGHT_PERIOD_MS) + LIGHT_PERIOD_MS) % LIGHT_PERIOD_MS;
}

/**
 * L'axe au vert à un instant donné, ou `null` pendant le tout-au-rouge.
 *
 * Fonction pure du temps et de la case : deux appels au même instant donnent la même
 * réponse, et rien n'a besoin d'être mémorisé entre deux images.
 */
export function greenAxis(col: number, row: number, now: number): Axis | null {
  const t = (((now + lightPhase(col, row)) % LIGHT_PERIOD_MS) + LIGHT_PERIOD_MS) % LIGHT_PERIOD_MS;
  if (t < LIGHT_CYCLE.greenMs) return 'ns';
  if (t < LIGHT_CYCLE.greenMs + LIGHT_CYCLE.clearMs) return null;
  if (t < 2 * LIGHT_CYCLE.greenMs + LIGHT_CYCLE.clearMs) return 'eo';
  return null;
}

/**
 * Un véhicule allant dans `direction` peut-il entrer dans la case contrôlée ?
 *
 * `stopSince` est l'instant où il s'est immobilisé, `null` s'il roule encore. C'est ce qui
 * distingue un STOP d'un feu : au stop on marque l'arrêt puis on passe, au feu on attend
 * le vert quelle que soit la durée déjà écoulée.
 */
export function mayEnter(options: {
  readonly control: Control;
  readonly col: number;
  readonly row: number;
  readonly direction: number;
  readonly now: number;
  readonly stopSince: number | null;
  /**
   * Axe de la branche principale du T, ou `null` si la case n'en a pas.
   *
   * Volontairement REQUIS et nullable plutôt qu'optionnel : l'appelant connaît toujours
   * le masque, donc il connaît toujours la réponse, et un paramètre qu'on peut oublier
   * ferait marquer l'arrêt à toute la ville sans que rien ne le signale.
   *
   * Au STOP, seule la branche SECONDAIRE marque l'arrêt : imposer l'arrêt à tout le monde
   * transformerait chaque T en bouchon, et ce n'est pas ce qu'un stop veut dire.
   */
  readonly majorAxis: Axis | null;
}): boolean {
  const { control, col, row, direction, now, stopSince, majorAxis } = options;
  if (control === null) return true;

  if (control.kind === 'light') return greenAxis(col, row, now) === axisOf(direction);

  // STOP : la route principale passe sans s'arrêter.
  if (majorAxis !== null && axisOf(direction) === majorAxis) return true;
  return stopSince !== null && now - stopSince >= STOP_WAIT_MS;
}

/** Durée de l'arrêt à un stop. Assez pour se lire comme un arrêt, pas assez pour lasser. */
export const STOP_WAIT_MS = 900;

/**
 * L'axe PRINCIPAL d'un T : celui dont les deux branches sont opposées.
 *
 * Un T a trois branches ; deux d'entre elles sont alignées et forment la route qui passe,
 * la troisième vient s'y raccorder. C'est la troisième qui porte le stop. Dérivé du
 * masque, donc juste par construction quelle que soit l'orientation du T.
 */
export function majorAxisOf(mask: number): Axis | null {
  const hasNS = (mask & NORTH) !== 0 && (mask & SOUTH) !== 0;
  const hasEO = (mask & EAST) !== 0 && (mask & WEST) !== 0;
  if (hasNS && !hasEO) return 'ns';
  if (hasEO && !hasNS) return 'eo';
  return null;
}

/**
 * Direction de la branche SECONDAIRE d'un T — celle qui porte le stop.
 *
 * C'est le seul bit du masque qui n'est pas sur l'axe principal. Dérivée, donc juste pour
 * les quatre orientations du T sans qu'on ait à les énumérer.
 *
 * Rend `null` si la case n'est pas un T : un carrefour complet n'a pas de branche
 * secondaire, et un virage n'a pas de branche du tout.
 */
export function minorBranchOf(mask: number): number | null {
  const major = majorAxisOf(mask);
  if (major === null) return null;

  const bits = [NORTH, EAST, SOUTH, WEST];
  for (let direction = 0; direction < 4; direction++) {
    const bit = bits[direction];
    if (bit === undefined || (mask & bit) === 0) continue;
    if (axisOf(direction) !== major) return direction;
  }
  return null;
}

/**
 * Décalage LATÉRAL d'un véhicule, en fraction de case, pour rouler à droite.
 *
 * ── Pourquoi une fraction et non des pixels ─────────────────────────────────
 *
 * Le moteur convertit les cases en pixels par une projection ; exprimer la voie en pixels
 * la ferait dériver au premier changement de géométrie de tuile. La demi-largeur de
 * chaussée mesurée vaut 0,19 case (`npm run measure:roads`), donc une voie centrée à mi-
 * chemin du bord et de l'axe tombe vers 0,10.
 *
 * ── Pourquoi ça compte pour le joueur ───────────────────────────────────────
 *
 * Sans voie, deux véhicules en sens inverse occupent exactement la même ligne et se
 * traversent. Avec, ils se croisent. C'est le détail qui fait qu'une rue se lit comme une
 * rue et non comme un rail.
 *
 * 0,13 et non 0,10 (18 août) : à 0,10 d'écart de part et d'autre de l'axe, deux
 * véhicules qui se croisent n'étaient séparés que de 0,20 case pour des carrosseries
 * de ~0,40 — ils se chevauchaient à chaque croisement (« superposition », Benoit).
 * 0,13 reste dans la demi-chaussée mesurée (0,19).
 */
export const LANE_OFFSET = 0.13;
